import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, PropsWithChildren, memo } from 'react';
import { FaCheck } from 'react-icons/fa';
import { Button } from '../form/Button';
import { StepStatus, useMultistep, useStep } from './useMultistep';

interface StepProps {
  id: string;
  title: string;
  description?: string;

  controls?: 'auto' | 'manual';
  validate?: () => Promise<boolean> | boolean;
  canSkipTo?: boolean;
}

interface StepButtonProps {
  canGoNext?: boolean;
}

export const StepButtons: FC<StepButtonProps> = ({ canGoNext: canGoNextOverride = true }) => {
  const { next, previous, canGoNext, canGoPrevious, finish, loading } = useMultistep(state => state);
  return (
    <div className="flex justify-between">
      {canGoPrevious ? (
        <Button className="disabled:opacity-30" onClick={previous} disabled={!canGoPrevious}>
          Previous
        </Button>
      ) : (
        <div></div>
      )}
      {canGoNext && (
        <Button className="disabled:opacity-30" onClick={next} disabled={!canGoNextOverride}>
          Next
        </Button>
      )}

      {!canGoNext && (
        <Button className="disabled:opacity-30" onClick={finish} loading={loading}>
          Finish
        </Button>
      )}
    </div>
  );
};

const variants = {
  completed: {
    opacity: 0.4,
    fontWeight: 600,
    fontSize: '1.2rem'
  },
  pending: {
    opacity: 0.7,
    fontWeight: 600,
    fontSize: '1.2rem'
  },
  current: {
    opacity: 1,
    fontWeight: 700,
    fontSize: '1.9rem'
  }
};

export const Step = memo(function Step({
  children,
  title,
  id,
  description,
  validate,
  controls = 'auto',
  canSkipTo
}: PropsWithChildren<StepProps>) {
  const { status } = useStep(id, validate);
  const skipToStep = useMultistep(state => state.skipToStep);
  
  const isClickable = canSkipTo && status !== StepStatus.Current;

  return (
    <motion.div className="border-secondary-std mb-6 border-b pb-4 last-of-type:border-0">
      <div className="flex justify-between">
        <motion.h2
          key={`${id}-title`}
          variants={variants}
          layout
          initial="pending"
          animate={status}
          className={clsx('text-primary-std', isClickable && 'cursor-pointer hover:text-primary-600 transition-colors')}
          onClick={() => {
            if (isClickable) skipToStep(id);
          }}
        >
          {title}
        </motion.h2>
        <AnimatePresence>
          {status == StepStatus.Completed && (
            <motion.div
              key={`${id}-completed`}
              initial={{ right: -40, opacity: 0 }}
              animate={{ right: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-primary-100 relative flex h-6 w-6 items-center justify-center rounded-full"
            >
              <FaCheck className="text-primary-700 h-3 w-3" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {status === StepStatus.Current && (
        <motion.div
          initial={{ bottom: -40, opacity: 0 }}
          animate={{ bottom: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="relative w-full"
        >
          {description && <p>{description}</p>}
          <div className="my-6">{children}</div>
          {controls === 'auto' && <StepButtons />}
        </motion.div>
      )}
    </motion.div>
  );
});
